/**
 * The command bar — one clickable footer for every screen and every input device.
 *
 * ## Why the hint line had to go
 *
 * Screens used to publish two different footers: a row of tappable chips on touch,
 * and a line of text like `I 性能詳細 · G GUNSMITH · ESC DEPLOY` everywhere else.
 * That text is a real convention, but it comes from consoles, where it is the only
 * option because there is no cursor and nothing on screen can be clicked. On a
 * desktop it inherits the look of a control while refusing to behave like one, and a
 * player reasonably tried to click it. Anything that looks like a button on a
 * machine that has a pointer has promised to be one.
 *
 * So the key label stays — it is how a player learns the shortcut — and the thing
 * carrying it becomes a real button. Keyboard players get the hint, mouse players
 * just click, and nothing on screen is lying.
 *
 * ## The other half of the reason
 *
 * Two footers meant two code paths, and I updated one without the other twice in a
 * day: the equipment screen got a chip on touch and nothing on desktop, and before
 * that the modal close buttons said "ESC" on a phone that has no ESC. One bar for
 * everything makes that class of bug unrepresentable rather than merely fixed.
 *
 * ## Devices
 *
 * Each item may name a keyboard code and a gamepad button. The badge shows whichever
 * the player is actually holding, and shows nothing at all on a touchscreen, where a
 * key hint would be noise. The *action* is identical in all three cases: synthesise
 * the key the screen's own state machine already handles.
 */

import { isTouchDevice } from '../core/Engine.js';
import { applyI18n, t } from './i18n.js';

/**
 * Build a command bar.
 *
 * @param {{key: string, code: string, pad?: string, primary?: boolean}[]} items
 *   `key` is an i18n key rather than a label — every screen using this rebuilds its
 *   footer on each render, so translating at build time needs no subscription.
 *   `code` is the keyboard code to synthesise. `pad` is a display label only.
 * @param {{padConnected?: boolean}} [opts]
 * @returns {HTMLElement} always a bar; there is no device that does not get one
 */
export function commandBar(items, opts = {}) {
  const touch = isTouchDevice();
  const pad = !!opts.padConnected;

  const bar = document.createElement('div');
  bar.className = 'cb-bar' + (touch ? ' is-touch' : '');

  for (const it of items) {
    const b = document.createElement('button');
    b.className = 'cb-btn' + (it.primary ? ' is-primary' : '');
    b.type = 'button';
    b.dataset.code = it.code;

    // The badge is a hint, so it appears only where the thing it names exists.
    const badge = touch ? null : pad && it.pad ? it.pad : keyLabel(it.code);
    if (badge) {
      const i = document.createElement('i');
      i.className = 'cb-key';
      i.textContent = badge;
      b.appendChild(i);
    }
    const s = document.createElement('span');
    s.dataset.i18n = it.key;
    b.appendChild(s);
    bar.appendChild(b);
  }
  applyI18n(bar);

  // One delegated listener, on `pointerup` rather than `click`: the 300 ms tap delay
  // is the difference between a menu that feels native and one that feels like a web
  // page. Works identically for a mouse.
  bar.addEventListener('pointerup', (e) => {
    const btn = e.target.closest?.('.cb-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    sendKey(btn.dataset.code);
  });
  // Swallow the click that follows, or a screen whose lists are click-driven gets a
  // second, unrelated activation from the same press.
  bar.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });

  return bar;
}

/**
 * Label for a modal's close button.
 *
 * These read "ESC" on every device once, which on a phone is not a control at all
 * but an instruction to press a key that does not exist — a player looking at it
 * correctly concluded there was no way back. Buttons are named for what they do; the
 * key that also does it is a hint, and only where that key exists.
 */
export function closeLabel() {
  return isTouchDevice() ? `✕ ${t('cmd.closeShort')}` : `ESC ✕`;
}

/** Dispatch one synthetic keystroke down the same path a real key takes. */
export function sendKey(code) {
  if (!code) return;
  const opts = { code, key: KEY_NAMES[code] ?? code, bubbles: true, cancelable: true };
  // On `document.body`, not `window`. A real keypress targets the focused element and
  // therefore runs capture -> target -> bubble, which is what lets these screens'
  // capture listeners claim it before `Input` does. Dispatching on `window` skips
  // capture, and both act on it — which is how one tap closed the loadout *and*
  // opened the pause menu.
  document.body.dispatchEvent(new KeyboardEvent('keydown', opts));
  document.body.dispatchEvent(new KeyboardEvent('keyup', opts));
}

/** What the badge says. Short, because it sits inside a button. */
const KEY_LABEL = {
  Escape: 'ESC',
  Enter: '⏎',
  Tab: 'TAB',
  Space: 'SPC',
  KeyG: 'G',
  KeyI: 'I',
  KeyR: 'R',
  KeyT: 'T',
  KeyX: 'X',
  KeyC: 'C',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/**
 * The badge for a key code.
 *
 * The table above listed letters one at a time, so adding a screen on a new letter
 * meant remembering to add a row — and forgetting produced a button whose badge
 * read literally `KeyM`, which is not a key any player can find on a keyboard.
 * `KeyX` and `DigitN` are derivable, so they are derived, and the table is left to
 * the codes that genuinely need a name.
 */
function keyLabel(code) {
  if (KEY_LABEL[code]) return KEY_LABEL[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return code.slice(6);
  return code;
}

/**
 * `key` for the codes we send. Screens switch on `e.code`, but `Input` and anything
 * downstream may read `e.key`, and a KeyboardEvent whose two halves disagree is a
 * nasty thing to debug later.
 */
const KEY_NAMES = {
  Escape: 'Escape',
  Enter: 'Enter',
  Tab: 'Tab',
  Space: ' ',
  KeyG: 'g',
  KeyI: 'i',
  KeyR: 'r',
  KeyT: 't',
  KeyX: 'x',
  KeyC: 'c',
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
};
